import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { adminAPI } from '../api/api';
import { Building2, Users as UsersIcon, FileText, Euro, TrendingUp, AlertCircle, Activity as ActivityIcon, ChevronRight } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Card, SectionHeader, StatTile, Badge, PlanBadge, formatDateTime, formatEuros, Spinner } from '../components/admin/AdminUI';

const Sparkline = ({ data, color = '#a78bfa' }) => {
  // `data` is { 'YYYY-MM-DD': count }. We render the last 30 days as a tiny SVG bar chart.
  const today = new Date();
  const days = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date(today); d.setDate(today.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    days.push({ key, count: data?.[key] || 0 });
  }
  const max = Math.max(1, ...days.map(d => d.count));
  const W = 240, H = 48, gap = 2, bw = (W - gap * (days.length - 1)) / days.length;
  return (
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
      {days.map((d, i) => {
        const h = Math.max(2, (d.count / max) * (H - 4));
        return (
          <rect
            key={d.key}
            x={i * (bw + gap)}
            y={H - h}
            width={bw}
            height={h}
            rx={1}
            fill={color}
            opacity={d.count === 0 ? 0.18 : 0.9}
          />
        );
      })}
    </svg>
  );
};

const AdminOverview = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [billing, setBilling] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [s, b, g, a] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.billingOverview(),
        adminAPI.growthStats(),
        adminAPI.globalAudit({ limit: 15 }),
      ]);
      setStats(s); setBilling(b); setGrowth(g); setAudit(a);
    } catch (err) {
      toast({ title: 'Erreur', description: 'Impossible de charger la vue d\'ensemble', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => { document.title = 'Skilldeck — Admin · Vue d\'ensemble'; }, []);

  if (loading) {
    return (
      <AdminLayout title="Vue d'ensemble">
        <div className="flex justify-center py-24"><Spinner /></div>
      </AdminLayout>
    );
  }

  const paid = (billing?.subscriptions_by_status?.active || 0) + (billing?.subscriptions_by_status?.trialing || 0);

  return (
    <AdminLayout title="Vue d'ensemble">
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">

        {/* KPI tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sd-stagger">
          <StatTile icon={Euro} label="MRR" value={billing?.mrr_eur || 0} formatter={formatEuros} accent="emerald"
            sublabel={`ARR ${formatEuros(billing?.arr_eur || 0)}`} />
          <StatTile icon={Building2} label="Organisations" value={stats?.total_organizations ?? 0} accent="violet"
            sublabel={`${paid} payantes`} />
          <StatTile icon={UsersIcon} label="Utilisateurs" value={stats?.total_users ?? 0} accent="blue" />
          <StatTile icon={FileText} label="Dossiers" value={stats?.total_dossiers ?? 0} accent="amber" />
        </div>

        {/* Health row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card>
            <SectionHeader icon={TrendingUp} title="Nouvelles organisations (30j)" />
            <div className="text-3xl font-bold text-white mb-2">{billing?.new_orgs_last_30d ?? 0}</div>
            <Sparkline data={growth?.orgs_per_day} color="#a78bfa" />
          </Card>
          <Card>
            <SectionHeader icon={FileText} title="Dossiers créés (30j)" />
            <div className="text-3xl font-bold text-white mb-2">
              {Object.values(growth?.dossiers_per_day || {}).reduce((a, b) => a + b, 0)}
            </div>
            <Sparkline data={growth?.dossiers_per_day} color="#60a5fa" />
          </Card>
          <Card>
            <SectionHeader icon={AlertCircle} title="État abonnements" />
            <div className="space-y-1.5 text-sm">
              <Row label="Actifs"     value={billing?.subscriptions_by_status?.active || 0}    tone="emerald" />
              <Row label="En essai"   value={billing?.subscriptions_by_status?.trialing || 0}  tone="blue" />
              <Row label="Impayés"    value={billing?.subscriptions_by_status?.past_due || 0}  tone="amber" />
              <Row label="Annulés"    value={billing?.subscriptions_by_status?.canceled || 0}  tone="gray" />
            </div>
          </Card>
        </div>

        {/* Plan breakdown */}
        <Card>
          <SectionHeader icon={Building2} title="Répartition par plan" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {['starter', 'pro', 'team', 'enterprise'].map((code) => (
              <div key={code} className="flex items-center justify-between px-4 py-3 rounded-lg border border-[rgba(108,71,255,.12)] bg-[rgba(108,71,255,.04)]">
                <PlanBadge code={code} />
                <span className="text-lg font-bold text-white">
                  {billing?.subscriptions_by_plan?.[code] ?? 0}
                </span>
              </div>
            ))}
          </div>
        </Card>

        {/* Recent activity */}
        <Card padding="p-0">
          <div className="px-5 py-4 border-b border-[rgba(108,71,255,.12)] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ActivityIcon className="h-4 w-4 text-violet-300" />
              <h3 className="text-sm font-semibold text-white">Activité récente</h3>
            </div>
            <button onClick={() => navigate('/admin/activity')} className="text-xs text-violet-300 hover:text-violet-200 inline-flex items-center gap-1">
              Voir tout <ChevronRight className="h-3 w-3" />
            </button>
          </div>
          {audit.length === 0 ? (
            <div className="text-center py-10 text-gray-500 text-sm">Aucune activité enregistrée</div>
          ) : (
            <ul className="divide-y divide-[rgba(108,71,255,.08)]">
              {audit.slice(0, 8).map((e) => (
                <li key={e.id} className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-[rgba(108,71,255,.04)] transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <Badge tone="violet">{e.action}</Badge>
                    <span className="text-sm text-gray-300 truncate">
                      {e.actor_name || 'Système'} · {e.organizations?.name || '—'}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 whitespace-nowrap">{formatDateTime(e.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
};

const Row = ({ label, value, tone }) => {
  const c = { emerald: 'text-emerald-300', blue: 'text-blue-300', amber: 'text-amber-300', gray: 'text-gray-400' }[tone] || 'text-gray-300';
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-400">{label}</span>
      <span className={`font-semibold ${c}`}>{value}</span>
    </div>
  );
};

export default AdminOverview;
